import { supabase } from '../lib/supabase';

export interface PlatformStats {
  totalUsers: number;
  activeGroups: number;
  totalRevenue: number;
  pendingPayments: number;
  totalContributions: number;
  activeSubscriptions: number;
}

export interface RevenueByPlan {
  planName: string;
  revenue: number;
  subscriptionCount: number;
}

export interface ActivityTrend {
  date: string;
  contributions: number;
  newUsers: number;
  revenue: number;
}

export interface TopGroup {
  id: string;
  name: string;
  memberCount: number;
  totalContributions: number;
  contributionAmount: number;
}

export interface AuditLog {
  id: string;
  userId: string | null;
  userName: string | null;
  actionType: string;
  actionDescription: string;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

export interface LoginCounts {
  todayUniqueUsers: number;
  weekUniqueUsers: number;
  todayEvents: number;
  weekEvents: number;
}

export const analyticsService = {
  // Get platform-wide statistics
  async getPlatformStats(): Promise<PlatformStats> {
    try {
      // Prefer the server-side RPC for super_admin (faster + consistent + RLS-safe).
      // Function defined in: 20251123142922_super_admin_audit_system.sql
      const { data: platformStats, error: platformStatsError } = await supabase.rpc('get_platform_statistics');
      if (!platformStatsError && platformStats) {
        // Get total contributions (super_admin is allowed read-only via RLS)
        const { count: totalContributions } = await supabase
          .from('contributions')
          .select('*', { count: 'exact', head: true });

        // Get active subscriptions count
        const { count: activeSubscriptions } = await supabase
          .from('user_subscriptions')
          .select('*', { count: 'exact', head: true })
          .eq('subscription_status', 'active');

        return {
          totalUsers: Number(platformStats.total_users || 0),
          activeGroups: Number(platformStats.active_groups || 0),
          totalRevenue: Number(platformStats.total_revenue || 0),
          pendingPayments: Number(platformStats.pending_payments || 0),
          totalContributions: totalContributions || 0,
          activeSubscriptions: activeSubscriptions || 0,
        };
      }

      // Get total users count
      const { count: totalUsers } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true });

      // Get active groups count
      const { count: activeGroups } = await supabase
        .from('groups')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);

      // Get total revenue from approved payments
      const { data: revenueData } = await supabase
        .from('payment_transactions')
        .select('amount_vatu')
        .eq('payment_status', 'approved');

      const totalRevenue = revenueData?.reduce((sum, payment) => sum + Number(payment.amount_vatu), 0) || 0;

      // Get pending payments count
      const { count: pendingPayments } = await supabase
        .from('payment_transactions')
        .select('*', { count: 'exact', head: true })
        .eq('payment_status', 'pending');

      // Get total contributions
      const { count: totalContributions } = await supabase
        .from('contributions')
        .select('*', { count: 'exact', head: true });

      // Get active subscriptions count
      const { count: activeSubscriptions } = await supabase
        .from('user_subscriptions')
        .select('*', { count: 'exact', head: true })
        .eq('subscription_status', 'active');

      return {
        totalUsers: totalUsers || 0,
        activeGroups: activeGroups || 0,
        totalRevenue,
        pendingPayments: pendingPayments || 0,
        totalContributions: totalContributions || 0,
        activeSubscriptions: activeSubscriptions || 0
      };
    } catch (error) {
      console.error('Error fetching platform stats:', error);
      throw error;
    }
  },

  // Get revenue breakdown by subscription plan
  async getRevenueByPlan(): Promise<RevenueByPlan[]> {
    try {
      // Prefer server-side RPC for super_admin (returns revenue + active subscription counts per plan)
      const { data: planStats, error: planStatsError } = await supabase.rpc('get_plan_statistics');
      if (!planStatsError && Array.isArray(planStats)) {
        return planStats.map((row: any) => ({
          planName: row.plan_name,
          revenue: Number(row.total_revenue || 0),
          subscriptionCount: Number(row.active_subscriptions || 0),
        }));
      }

      const { data, error } = await supabase
        .from('payment_transactions')
        .select(`
          amount_vatu,
          user_subscriptions!inner(
            subscription_plans!inner(
              plan_name
            )
          )
        `)
        .eq('payment_status', 'approved');

      if (error) throw error;

      // Aggregate revenue by plan
      const revenueMap = new Map<string, { revenue: number; count: number }>();
      
      data?.forEach((payment: any) => {
        const planName = payment.user_subscriptions?.subscription_plans?.plan_name;
        if (planName) {
          const current = revenueMap.get(planName) || { revenue: 0, count: 0 };
          revenueMap.set(planName, {
            revenue: current.revenue + Number(payment.amount_vatu),
            count: current.count + 1
          });
        }
      });

      return Array.from(revenueMap.entries()).map(([planName, stats]) => ({
        planName,
        revenue: stats.revenue,
        subscriptionCount: stats.count
      }));
    } catch (error) {
      console.error('Error fetching revenue by plan:', error);
      throw error;
    }
  },

  // Get activity trends over time
  async getActivityTrends(days: number = 30): Promise<ActivityTrend[]> {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      // Get contributions by date
      const { data: contributionsData } = await supabase
        .from('contributions')
        .select('contribution_date, amount')
        .gte('contribution_date', startDate.toISOString());

      // Get new users by date
      const { data: usersData } = await supabase
        .from('user_profiles')
        .select('created_at')
        .gte('created_at', startDate.toISOString());

      // Get approved payments by date
      const { data: paymentsData } = await supabase
        .from('payment_transactions')
        .select('approved_at, amount_vatu')
        .eq('payment_status', 'approved')
        .gte('approved_at', startDate.toISOString());

      // Create date map for aggregation
      const trendsMap = new Map<string, ActivityTrend>();

      // Initialize all dates with zero values
      for (let i = 0; i < days; i++) {
        const date = new Date(startDate);
        date.setDate(date.getDate() + i);
        const dateKey = date.toISOString().split('T')[0];
        trendsMap.set(dateKey, {
          date: dateKey,
          contributions: 0,
          newUsers: 0,
          revenue: 0
        });
      }

      // Aggregate contributions
      contributionsData?.forEach((contribution: any) => {
        const dateKey = new Date(contribution.contribution_date).toISOString().split('T')[0];
        const trend = trendsMap.get(dateKey);
        if (trend) {
          trend.contributions += 1;
        }
      });

      // Aggregate new users
      usersData?.forEach((user: any) => {
        const dateKey = new Date(user.created_at).toISOString().split('T')[0];
        const trend = trendsMap.get(dateKey);
        if (trend) {
          trend.newUsers += 1;
        }
      });

      // Aggregate revenue
      paymentsData?.forEach((payment: any) => {
        const dateKey = new Date(payment.approved_at).toISOString().split('T')[0];
        const trend = trendsMap.get(dateKey);
        if (trend) {
          trend.revenue += Number(payment.amount_vatu);
        }
      });

      return Array.from(trendsMap.values()).sort((a, b) => a.date.localeCompare(b.date));
    } catch (error) {
      console.error('Error fetching activity trends:', error);
      throw error;
    }
  },

  // Get top active groups
  async getTopGroups(limit: number = 10): Promise<TopGroup[]> {
    try {
      // Prefer server-side aggregation when available (super_admin only).
      const { data: topGroups, error: topGroupsError } = await supabase.rpc('get_top_groups', {
        p_limit: limit,
      });

      if (!topGroupsError && Array.isArray(topGroups)) {
        return topGroups.map((g: any) => ({
          id: g.id,
          name: g.name,
          memberCount: Number(g.member_count || 0),
          totalContributions: Number(g.total_contributions || 0),
          contributionAmount: Number(g.contribution_amount || 0),
        }));
      }

      const { data, error } = await supabase
        .from('groups')
        .select(`
          id,
          name,
          contributions(count),
          contributions(amount)
        `)
        .eq('is_active', true)
        .limit(limit);

      if (error) throw error;

      const groups: TopGroup[] = data?.map((group: any) => ({
        id: group.id,
        name: group.name,
        memberCount: 0, // Will be populated separately
        totalContributions: group.contributions?.length || 0,
        contributionAmount: group.contributions?.reduce((sum: number, c: any) => sum + Number(c.amount || 0), 0) || 0
      })) || [];

      // Get member counts for each group
      await Promise.all(groups.map(async (group) => {
        const { count } = await supabase
          .from('group_members')
          .select('*', { count: 'exact', head: true })
          .eq('group_id', group.id);
        group.memberCount = count || 0;
      }));

      return groups.sort((a, b) => b.contributionAmount - a.contributionAmount);
    } catch (error) {
      console.error('Error fetching top groups:', error);
      throw error;
    }
  },

  // Get recent audit logs
  async getAuditLogs(limit: number = 100): Promise<AuditLog[]> {
    try {
      const { data, error } = await supabase
        .from('audit_logs')
        .select(`
          id,
          user_id,
          action_type,
          action_description,
          ip_address,
          user_agent,
          created_at,
          user_profiles(full_name)
        `)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return data?.map((log: any) => ({
        id: log.id,
        userId: log.user_id,
        userName: log.user_profiles?.full_name || null,
        actionType: log.action_type,
        actionDescription: log.action_description,
        ipAddress: log.ip_address,
        userAgent: log.user_agent,
        createdAt: log.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      throw error;
    }
  }
  ,

  // Get successful login counts (today / this week)
  async getLoginCounts(): Promise<LoginCounts> {
    const { data, error } = await supabase.rpc('get_login_counts');
    if (error) throw error;

    return {
      todayUniqueUsers: Number((data as any)?.today_unique_users || 0),
      weekUniqueUsers: Number((data as any)?.week_unique_users || 0),
      todayEvents: Number((data as any)?.today_events || 0),
      weekEvents: Number((data as any)?.week_events || 0),
    };
  }
};